import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFavicon } from '../useFavicon';
import { TextField, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, IconButton, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Snackbar, Alert, MenuItem, Tooltip, Box, Checkbox } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import QrCodeIcon from '@mui/icons-material/QrCode';
import DownloadIcon from '@mui/icons-material/Download';
import youtubeApi from '../api/youtubeApi';

const YOUTUBE_FAVICON_URL = process.env.PUBLIC_URL + "/youtube.ico";

const TonalityAddYoutubePage = () => {
  useFavicon(YOUTUBE_FAVICON_URL); // Set the favicon for this page
  const navigate = useNavigate();

  const [videos, setVideos] = useState([]);
  const [videoData, setVideoData] = useState({
    name: '',
    videoId: '',
    customStartTime: '',
    customEndTime: '',
    baseStartTime: '',
    durationFactor: '',
    tempoStartTime: '',
    timeSignature: '',
    beatDuration: '',
    intervals: '[]' // Ensure intervals is initialized as a JSON string of an empty array
  });
  const [jsonInput, setJsonInput] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editVideoId, setEditVideoId] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [displayedQRCodes, setDisplayedQRCodes] = useState(new Set());
  const [qrCodeDataUrls, setQRCodeDataUrls] = useState({});
  const [loadingQRCodes, setLoadingQRCodes] = useState(new Set());
  const [selectedIntervals, setSelectedIntervals] = useState(new Set());
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [insertPosition, setInsertPosition] = useState('end');

  useEffect(() => {
    fetchVideos();
  }, []);
  const fetchVideos = async () => {
    try {
      const response = await youtubeApi.getAll();
      setVideos(response.data);
    } catch (error) {
      const errorMessage = error.response?.data?.details || error.response?.data?.error || error.message;
      handleSnackbarOpen(`Error fetching videos: ${errorMessage}`, 'error');
    }
  };
  const handleAddVideo = async (e) => {
    e.preventDefault();
    try {
      const newVideo = { 
        ...videoData, 
        intervals: videoData.intervals ? JSON.parse(videoData.intervals) : [] 
      };
      await youtubeApi.create(newVideo);
      setVideoData({
        name: '',
        videoId: '',
        customStartTime: '',
        customEndTime: '',
        baseStartTime: '',
        durationFactor: '',
        tempoStartTime: '',
        timeSignature: '',
        beatDuration: '',
        intervals: '[]'
      });
      fetchVideos(); // Refresh the videos after adding a new one
      handleSnackbarOpen('Video added successfully', 'success');
    } catch (error) {
      const errorMessage = error.response?.data?.details || error.response?.data?.error || error.message;
      handleSnackbarOpen(`Error adding video: ${errorMessage}`, 'error');
    }
  };
  const handleAddJsonVideo = async (e) => {
    e.preventDefault();
    try {
      const newVideo = JSON.parse(jsonInput);
      console.log('Parsed JSON video data:', newVideo); // Debug message
      await youtubeApi.create(newVideo);
      setJsonInput('');
      fetchVideos(); // Refresh the videos after adding a new one
      handleSnackbarOpen('Video added successfully', 'success');
    } catch (error) {
      const errorMessage = error.response?.data?.details || error.response?.data?.error || error.message;
      handleSnackbarOpen(`Error adding video: ${errorMessage}`, 'error');
    }
  };
  const handleDeleteVideo = async (id) => {
    try {
      await youtubeApi.delete(id);
      fetchVideos(); // Refresh the videos after deleting one
      handleSnackbarOpen('Video deleted successfully', 'success');
    } catch (error) {
      const errorMessage = error.response?.data?.details || error.response?.data?.error || error.message;
      handleSnackbarOpen(`Error deleting video: ${errorMessage}`, 'error');
    }
  };

  const handleEditVideo = (video) => {
    setEditVideoId(video._id);
    setVideoData({ ...video, intervals: JSON.stringify(video.intervals || []) }); // Ensure intervals is a JSON string
    setSelectedIntervals(new Set());
    setEditDialogOpen(true);
  };
  const handleUpdateVideo = async () => {
    try {
      const updatedVideo = { 
        ...videoData, 
        intervals: videoData.intervals ? JSON.parse(videoData.intervals) : [] 
      };
      await youtubeApi.update(editVideoId, updatedVideo);
      setEditDialogOpen(false);
      fetchVideos(); // Refresh the videos after updating one
      handleSnackbarOpen('Video updated successfully', 'success');
    } catch (error) {
      const errorMessage = error.response?.data?.details || error.response?.data?.error || error.message;
      handleSnackbarOpen(`Error updating video: ${errorMessage}`, 'error');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setVideoData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleIntervalChange = (index, field, value) => {
    const updatedIntervals = videoData.intervals ? JSON.parse(videoData.intervals) : [];
    updatedIntervals[index][field] = value;
    setVideoData((prevData) => ({ ...prevData, intervals: JSON.stringify(updatedIntervals) }));
  };

  const handleAddInterval = () => {
    const updatedIntervals = JSON.parse(videoData.intervals);
    updatedIntervals.push({ duration: 4, position: 'I', Modulation: false, comment: '' }); // Add comment field
    setVideoData((prevData) => ({ ...prevData, intervals: JSON.stringify(updatedIntervals) }));
  };

  const handleRemoveInterval = (index) => {
    const updatedIntervals = videoData.intervals ? JSON.parse(videoData.intervals) : [];
    updatedIntervals.splice(index, 1);
    setVideoData((prevData) => ({ ...prevData, intervals: JSON.stringify(updatedIntervals) }));
    setSelectedIntervals((prev) => {
      const next = new Set();
      prev.forEach((selectedIndex) => {
        if (selectedIndex === index) return;
        next.add(selectedIndex > index ? selectedIndex - 1 : selectedIndex);
      });
      return next;
    });
  };

  const handleToggleIntervalSelection = (index) => {
    setSelectedIntervals((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const handleDuplicateSelectedIntervals = () => {
    if (selectedIntervals.size === 0) return;
    setDuplicateDialogOpen(true);
  };

  const handleConfirmDuplicate = () => {
    const updatedIntervals = JSON.parse(videoData.intervals);
    if (!updatedIntervals.length || selectedIntervals.size === 0) return;

    const selectedSet = new Set(selectedIntervals);
    const duplicates = [];
    
    // Collect duplicates
    updatedIntervals.forEach((interval, index) => {
      if (selectedSet.has(index)) {
        duplicates.push({ ...interval });
      }
    });

    let resultIntervals;
    if (insertPosition === 'end') {
      // Add at the end
      resultIntervals = [...updatedIntervals, ...duplicates];
    } else if (insertPosition === 'start') {
      // Add at the beginning
      resultIntervals = [...duplicates, ...updatedIntervals];
    } else if (insertPosition === 'after') {
      // Insert after each selected interval
      resultIntervals = [];
      updatedIntervals.forEach((interval, index) => {
        resultIntervals.push(interval);
        if (selectedSet.has(index)) {
          resultIntervals.push({ ...interval });
        }
      });
    } else {
      // Custom position (numeric index)
      const position = parseInt(insertPosition);
      resultIntervals = [
        ...updatedIntervals.slice(0, position),
        ...duplicates,
        ...updatedIntervals.slice(position)
      ];
    }

    setVideoData((prevData) => ({ ...prevData, intervals: JSON.stringify(resultIntervals) }));
    setSelectedIntervals(new Set());
    setDuplicateDialogOpen(false);
    setInsertPosition('end');
  };

  const handleSnackbarOpen = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setSnackbarMessage('');
  };

  const normalizeId = (id) => (id ? String(id).trim() : '');

  const handleToggleQRCode = async (customVideoId) => {
    const normalizedId = normalizeId(customVideoId);
    if (!normalizedId) {
      handleSnackbarOpen('Missing video ID for QR code', 'error');
      return;
    }

    if (displayedQRCodes.has(normalizedId)) {
      setDisplayedQRCodes((prev) => {
        const next = new Set(prev);
        next.delete(normalizedId);
        return next;
      });

      setQRCodeDataUrls((prev) => {
        const next = { ...prev };
        delete next[normalizedId];
        return next;
      });

      return;
    }

    if (!qrCodeDataUrls[normalizedId] && !loadingQRCodes.has(normalizedId)) {
      try {
        setLoadingQRCodes((prev) => new Set(prev).add(normalizedId));
        const response = await youtubeApi.getQRCode(normalizedId);
        setQRCodeDataUrls((prev) => ({
          ...prev,
          [normalizedId]: response.data
        }));
      } catch (error) {
        handleSnackbarOpen(`Error loading QR code: ${error.message}`, 'error');
      } finally {
        setLoadingQRCodes((prev) => {
          const next = new Set(prev);
          next.delete(normalizedId);
          return next;
        });
      }
    }

    setDisplayedQRCodes((prev) => new Set(prev).add(normalizedId));
  };

  const handleDownloadQRCode = (customVideoId) => {
    const normalizedId = normalizeId(customVideoId);
    const qrData = qrCodeDataUrls[normalizedId];
    if (!qrData?.qrCodeDataUrl) {
      handleSnackbarOpen('QR code not loaded yet', 'error');
      return;
    }

    const link = document.createElement('a');
    link.download = `qr-${normalizedId}.png`;
    link.href = qrData.qrCodeDataUrl;
    link.click();
  };

  // Format a time point (in seconds) to MM:SS format
  const formatTimePoint = (timeInSeconds) => {
    if (timeInSeconds === undefined || timeInSeconds === null) return 'N/A';
    
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Format intervals for display
  const formatIntervals = (intervals) => {
    if (!intervals || intervals.length === 0) return 'None';
    return intervals.map((interval, index) => 
      `${index + 1}: ${formatTimePoint(interval.start)} - ${formatTimePoint(interval.end)}`
    ).join(', ');
  };

  // Navigate back to main page
  const handleBackToMain = () => {
    navigate('/');
  };

  return (
    <div style={{ padding: '20px' }}>
      <Typography variant="h4" gutterBottom>Add YouTube Video</Typography>
      <Button variant="contained" color="secondary" onClick={handleBackToMain} style={{ marginBottom: '20px' }}>
        Back to Main
      </Button>
      <form onSubmit={handleAddVideo} style={{ marginBottom: '20px' }}>
        <TextField label="Name" name="name" value={videoData.name} onChange={handleChange} required style={{ marginRight: '10px' }} />
        <TextField label="Video ID" name="videoId" value={videoData.videoId} onChange={handleChange} required style={{ marginRight: '10px' }} />
        <TextField label="Custom Start Time" name="customStartTime" value={videoData.customStartTime} onChange={handleChange} required style={{ marginRight: '10px' }} />
        <TextField label="Custom End Time" name="customEndTime" value={videoData.customEndTime} onChange={handleChange} required style={{ marginRight: '10px' }} />
        <TextField label="Base Start Time" name="baseStartTime" value={videoData.baseStartTime} onChange={handleChange} required style={{ marginRight: '10px' }} />
        <TextField label="Duration Factor" name="durationFactor" value={videoData.durationFactor} onChange={handleChange} required style={{ marginRight: '10px' }} />
        <TextField label="Tempo Start Time" name="tempoStartTime" value={videoData.tempoStartTime} onChange={handleChange} required style={{ marginRight: '10px' }} />
        <TextField label="Time Signature" name="timeSignature" value={videoData.timeSignature} onChange={handleChange} required style={{ marginRight: '10px' }} />
        <TextField label="Beat Duration" name="beatDuration" value={videoData.beatDuration} onChange={handleChange} required style={{ marginRight: '10px' }} />
        <TextField label="Intervals (JSON)" name="intervals" value={videoData.intervals} onChange={handleChange} required style={{ marginRight: '10px' }} />
        <Button type="submit" variant="contained" color="primary">Add Video</Button>
      </form>
      <form onSubmit={handleAddJsonVideo} style={{ marginBottom: '20px' }}>
        <TextField
          label="JSON Input"
          value={jsonInput}
          onChange={(e) => setJsonInput(e.target.value)}
          required
          fullWidth
          multiline
          rows={4}
          variant="outlined"
        />
        <Button type="submit" variant="contained" color="primary" style={{ marginTop: '10px' }}>Add Video from JSON</Button>
      </form>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#86aae3' }}>
              <TableCell sx={{ fontWeight: 'bold', border: '1px solid #ddd' }}>#</TableCell>
              <TableCell sx={{ fontWeight: 'bold', border: '1px solid #ddd' }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold', border: '1px solid #ddd' }}>Video ID</TableCell>
              <TableCell sx={{ fontWeight: 'bold', border: '1px solid #ddd' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {videos.map((video, index) => {
              const qrKey = video.customVideoId || video._id;
              const normalizedId = normalizeId(qrKey);
              const isQRCodeVisible = displayedQRCodes.has(normalizedId);
              const isQRCodeLoading = loadingQRCodes.has(normalizedId);
              const qrData = qrCodeDataUrls[normalizedId];

              return (
                <React.Fragment key={video._id || index}>
                  <TableRow sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' } }}>
                    <TableCell sx={{ border: '1px solid #ddd' }}>{index + 1}</TableCell>
                    <TableCell sx={{ border: '1px solid #ddd' }}>{video.name}</TableCell>
                    <TableCell sx={{ border: '1px solid #ddd' }}>{video.videoId}</TableCell>
                    <TableCell sx={{ border: '1px solid #ddd' }}>
                      <Tooltip title="Edit" arrow>
                        <IconButton onClick={() => handleEditVideo(video)} color="primary">
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete" arrow>
                        <IconButton onClick={() => handleDeleteVideo(video._id)} color="secondary">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={isQRCodeVisible ? 'Hide QR Code' : 'Show QR Code'} arrow>
                        <span>
                          <IconButton
                            onClick={() => handleToggleQRCode(qrKey)}
                            color={isQRCodeVisible ? 'default' : 'primary'}
                            disabled={isQRCodeLoading}
                          >
                            <QrCodeIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Download QR Code" arrow>
                        <span>
                          <IconButton
                            onClick={() => handleDownloadQRCode(qrKey)}
                            disabled={!qrData?.qrCodeDataUrl}
                          >
                            <DownloadIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  {isQRCodeVisible && qrData && (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ padding: '20px', backgroundColor: '#f8f9fa' }}>
                        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                          <Box sx={{ flex: 1, minWidth: 240 }}>
                            <Typography variant="h6" gutterBottom>
                              QR Code for {qrData.videoTitle || video.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Scan to open this video directly in the display page.
                            </Typography>
                            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                              {qrData.videoUrl}
                            </Typography>
                          </Box>
                          <Box sx={{ flexShrink: 0, textAlign: 'center' }}>
                            <img
                              src={qrData.qrCodeDataUrl}
                              alt={`QR Code for ${qrData.videoTitle || video.name}`}
                              style={{
                                width: '150px',
                                height: '150px',
                                border: '2px solid #e9ecef',
                                borderRadius: '8px',
                                backgroundColor: 'white',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                              }}
                            />
                            <Box sx={{ mt: 1 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={() => handleDownloadQRCode(qrKey)}
                              >
                                Download
                              </Button>
                            </Box>
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Back to Main Page Button */}
      <div style={{ display: 'flex', justifyContent: 'center', margin: '20px 0' }}>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleBackToMain}
          style={{ minWidth: '200px' }}
        >
          Back to Main Page
        </Button>
      </div>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>Edit YouTube Video</DialogTitle>
        <DialogContent>
          <DialogContentText>
            To edit this video, please modify the fields below.
          </DialogContentText>
          <TextField autoFocus margin="dense" label="Name" name="name" value={videoData.name} onChange={handleChange} fullWidth />
          <TextField margin="dense" label="Video ID" name="videoId" value={videoData.videoId} onChange={handleChange} fullWidth />
          <TextField margin="dense" label="Custom Start Time" name="customStartTime" value={videoData.customStartTime} onChange={handleChange} fullWidth />
          <TextField margin="dense" label="Custom End Time" name="customEndTime" value={videoData.customEndTime} onChange={handleChange} fullWidth />
          <TextField margin="dense" label="Base Start Time" name="baseStartTime" value={videoData.baseStartTime} onChange={handleChange} fullWidth />
          <TextField margin="dense" label="Duration Factor" name="durationFactor" value={videoData.durationFactor} onChange={handleChange} fullWidth />
          <TextField margin="dense" label="Tempo Start Time" name="tempoStartTime" value={videoData.tempoStartTime} onChange={handleChange} fullWidth />
          <TextField margin="dense" label="Time Signature" name="timeSignature" value={videoData.timeSignature} onChange={handleChange} fullWidth />
          <TextField margin="dense" label="Beat Duration" name="beatDuration" value={videoData.beatDuration} onChange={handleChange} fullWidth />
          <Typography variant="h6" gutterBottom>Intervals</Typography>
          <datalist id="duration-options">
            <option value="1" />
            <option value="2" />
            <option value="3" />
            <option value="4" />
            <option value="8" />
          </datalist>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <Button
              variant="outlined"
              onClick={handleDuplicateSelectedIntervals}
              disabled={selectedIntervals.size === 0}
            >
              Duplicate Selected
            </Button>
          </Box>
          {JSON.parse(videoData.intervals).map((interval, index) => (
            <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <Checkbox
                checked={selectedIntervals.has(index)}
                onChange={() => handleToggleIntervalSelection(index)}
                sx={{ mr: 1 }}
              />
              <Typography variant="body1" style={{ marginRight: '10px' }}>{index + 1}</Typography>
              <TextField
                label="Duration"
                value={interval.duration}
                onChange={(e) => handleIntervalChange(index, 'duration', e.target.value)}
                style={{ marginRight: '10px', flex: 1, textAlign: 'center' }}
                inputProps={{ list: 'duration-options', style: { textAlign: 'center' } }}
              />
              <TextField
                label="Position"
                value={interval.position}
                onChange={(e) => handleIntervalChange(index, 'position', e.target.value)}
                style={{ marginRight: '10px', flex: 1, textAlign: 'center' }}
                inputProps={{ style: { textAlign: 'center' } }}
                select
              >
                {['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', '-'].map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Modulation"
                value={interval.Modulation}
                onChange={(e) => handleIntervalChange(index, 'Modulation', e.target.value)}
                style={{ marginRight: '10px', flex: 1 }}
              />
              <TextField
                label="Comment"
                value={interval.comment}
                onChange={(e) => handleIntervalChange(index, 'comment', e.target.value)}
                style={{ marginRight: '10px', flex: 1 }}
              />
              <Button onClick={() => handleRemoveInterval(index)} color="secondary" style={{ flex: 0 }}>
                Remove
              </Button>
            </div>
          ))}
          <Button onClick={handleAddInterval} color="primary">Add Interval</Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleUpdateVideo} color="primary">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={duplicateDialogOpen} onClose={() => setDuplicateDialogOpen(false)}>
        <DialogTitle>Duplicate Selected Intervals</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Where would you like to insert the {selectedIntervals.size} duplicated interval(s)?
          </DialogContentText>
          <TextField
            select
            fullWidth
            margin="dense"
            label="Insert Position"
            value={insertPosition}
            onChange={(e) => setInsertPosition(e.target.value)}
          >
            <MenuItem value="start">At the beginning</MenuItem>
            <MenuItem value="end">At the end</MenuItem>
            <MenuItem value="after">After each selected interval</MenuItem>
            {JSON.parse(videoData.intervals || '[]').map((_, index) => (
              <MenuItem key={index} value={index.toString()}>
                At position {index + 1}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDuplicate} color="primary" variant="contained">
            Duplicate
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default TonalityAddYoutubePage;